import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import { formatCount, formatTimestamp, statusLabel, statusValues } from '@saay/formatting';
import {
  AccordionPanel,
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  DialogActions,
  ErrorState,
  FactList,
  FieldErrorList,
  PageHeader,
  ReasonChips,
  Skeleton,
  StatusChip,
  Tabs,
  TextArea,
  useToast,
} from '../kit.js';
import { ListRow } from '@saay/blocks';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon, CircleCheckIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';

import type {
  ApprovalCancellation,
  ApprovalDecided,
  ApprovalDecision,
  ApprovalRejection,
  ApprovalRequest,
  ApprovalRequestSummary,
} from '../api.js';
import { TimeRemaining } from '../components/admin-feedback.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { ApprovalStatus, ImpactReport, StoredFields } from '../components/approval-view.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListFooter, ListLoadMore } from '../components/list-footer.js';
import { ClearFiltersButton, ListEmpty, ListSkeleton } from '../components/list-state.js';
import { OPENED_FROM_LIST, onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { SurfaceSection } from '../components/surface-section.js';
import { canCancelApproval, canDecide, canUse } from '../permissions.js';
import {
  type ApprovalFilters,
  approvalPath,
  listStatus,
  setupKeys,
  useAdminDashboard,
  useApproval,
  useApprovals,
} from '../queries.js';
import {
  canSeeMerchantRequests,
  MerchantRequestsView,
  useMerchantRequestCount,
} from './merchant-requests.js';
import { readApprovalsView, storeApprovalsView } from '../device-prefs.js';
import { reasonPresetsForIntent } from '../reason-presets.js';
import { reasonSchema } from '../validation.js';

/**
 * The approval inbox (AC §18.2, rows A1–A5; FTD §9.2; D-22). The prototype has no inbox page, so it
 * uses the panel design system: the page header, the compact toolbar, the list card, and the wide
 * drawer for the detail. Nothing is decided here: the server's `decidable` flag gates the buttons,
 * every decision is shown only after the server answered, and a refused or stale decision reloads
 * the request.
 */

type View = 'all' | 'decidable' | 'requested';

export interface ApprovalsOutletContext {
  readonly self: AdminSelf;
}

function readFilters(params: URLSearchParams): ApprovalFilters {
  const view = params.get('view');
  const status = params.get('status');
  const actionType = params.get('actionType');
  const sort = params.get('sort');
  return {
    ...(view === 'decidable' || view === 'requested' ? { view } : {}),
    ...(status === null || status === '' ? {} : { status }),
    ...(actionType === null || actionType === '' ? {} : { actionType }),
    ...(sort === 'expiresAt' ? { sort } : {}),
  };
}

function approvalSheetTitle(
  request: Pick<ApprovalRequest, 'actionType' | 'targetDisplayFa' | 'targetType'>,
): string {
  const action = statusLabel('approvalActionType', request.actionType);
  const target = request.targetDisplayFa ?? statusLabel('approvalTargetType', request.targetType);
  return `${action} · ${target}`;
}

export function ApprovalsPage() {
  const { pathname } = useLocation();
  usePageTitle('درخواست‌ها', pathname.replace(/\/$/, '') === '/approvals');
  return <RequireArea area="approvals">{(self) => <RequestsInbox self={self} />}</RequireArea>;
}

type InboxTab = 'approvals' | 'merchant';

function tabLabel(base: string, count: number, more = false): string {
  return count > 0 || more ? `${base} (${formatCount(count)}${more ? '+' : ''})` : base;
}

/**
 * «درخواست‌ها»: one place for everything waiting on a platform admin (owner requirement). The
 * «تأییدهای دومرحله‌ای» tab is the approval inbox (A1); «درخواست‌های فروشگاه» lists the requests
 * merchants submit from merchant-web (branch requests, payout account changes). The tab is in the
 * URL (`?kind=merchant`), so a reload or a shared link keeps it.
 */
function RequestsInbox({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const merchantTab = canSeeMerchantRequests(self);
  const tab: InboxTab = merchantTab && params.get('kind') === 'merchant' ? 'merchant' : 'approvals';
  const dashboard = useAdminDashboard();
  const decidable = dashboard.data?.approvals.decidableCount ?? 0;
  const merchantCount = useMerchantRequestCount(self);
  return (
    <>
      <PageHeader title="درخواست‌ها" />
      {merchantTab ? (
        <Tabs<InboxTab>
          label="نوع درخواست‌ها"
          onChange={(next) => setParams(next === 'merchant' ? { kind: 'merchant' } : {})}
          panel={
            tab === 'merchant' ? (
              <MerchantRequestsView self={self} />
            ) : (
              <ApprovalsView self={self} />
            )
          }
          tabs={[
            { key: 'approvals', label: tabLabel('تأییدهای دومرحله‌ای', decidable) },
            {
              key: 'merchant',
              label: tabLabel('درخواست‌های فروشگاه', merchantCount.count, merchantCount.more),
            },
          ]}
          value={tab}
        />
      ) : (
        <ApprovalsView self={self} />
      )}
      <Outlet context={{ self } satisfies ApprovalsOutletContext} />
    </>
  );
}

function targetLabel(row: Pick<ApprovalRequestSummary, 'targetDisplayFa' | 'targetType'>): string {
  return row.targetDisplayFa ?? statusLabel('approvalTargetType', row.targetType);
}

function ApprovalDeadline({ expiresAt }: { readonly expiresAt: string }) {
  const urgent =
    Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000)) > 0 &&
    Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000)) <= 24 * 60 * 60;
  return (
    <TimeRemaining
      expiresAt={expiresAt}
      label={urgent ? 'مهلت تصمیم' : 'مهلت'}
      {...(urgent ? {} : { expiredLabel: 'مهلت گذشته' })}
    />
  );
}

function ApprovalsView({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const decider = canUse(self, 'approveApprovalRequest');
  // The default view applies from the first render, so the list is fetched once with it (a bare
  // first fetch was sent, then dropped when the URL took the default).
  const defaults = useMemo(() => {
    const stored = readApprovalsView();
    const next = new URLSearchParams();
    if (decider) {
      next.set('view', stored === 'requested' || stored === 'all' ? stored : 'decidable');
      next.set('sort', 'expiresAt');
    } else if (stored === 'requested' || stored === 'all') {
      next.set('view', stored);
    }
    return next;
  }, [decider]);
  const bare = params.toString() === '';
  const filters = readFilters(bare ? defaults : params);
  useEffect(() => {
    if (bare && defaults.toString() !== '') {
      setParams(defaults, { replace: true });
    }
  }, [bare, defaults, setParams]);
  const approvals = useApprovals(filters);
  const rows = approvals.data?.pages.flatMap((page) => page.items) ?? [];
  const filtered = Object.keys(filters).some((key) => key !== 'sort');
  const requester = canUse(self, 'cancelApprovalRequest');
  const views: { key: View; label: string }[] = [
    { key: 'all', label: 'همه' },
    ...(decider ? [{ key: 'decidable' as const, label: 'منتظر تصمیم من' }] : []),
    ...(requester ? [{ key: 'requested' as const, label: 'درخواست‌های من' }] : []),
  ];
  const open = (row: ApprovalRequestSummary) => ({ pathname: `/approvals/${row.id}`, search });

  function change(changes: Readonly<Record<string, string>>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    if ('view' in changes) {
      const view = changes.view;
      storeApprovalsView(view === '' || view === 'all' ? 'all' : (view as View));
    }
    setParams(next);
  }

  return (
    <SurfaceSection id="approval-list" title="فهرست درخواست‌ها">
      <FilterPillBar
        activeFilterCount={
          [filters.status, filters.actionType, filters.view].filter(Boolean).length
        }
        label="فیلتر درخواست‌های تأیید"
        onClearAll={() => change({ actionType: '', status: '', sort: '', view: '' })}
      >
        {views.length > 1 ? (
          <FilterPill
            emptyLabel="همه درخواست‌ها"
            label="نمای درخواست‌ها"
            onChange={(view) => change({ view })}
            options={views
              .filter((view) => view.key !== 'all')
              .map((view) => ({ label: view.label, value: view.key }))}
            value={filters.view ?? ''}
          />
        ) : null}
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={(status) => change({ status })}
          options={statusValues('approvalRequestStatus').map((value) => ({
            label: statusLabel('approvalRequestStatus', value),
            value,
          }))}
          value={filters.status ?? ''}
        />
        <FilterPill
          emptyLabel="همه نوع‌ها"
          label="نوع درخواست"
          onChange={(actionType) => change({ actionType })}
          options={statusValues('approvalActionType').map((value) => ({
            label: statusLabel('approvalActionType', value),
            value,
          }))}
          value={filters.actionType ?? ''}
        />
      </FilterPillBar>
      <QueryState
        empty={
          <ListEmpty
            {...(filtered
              ? {
                  action: (
                    <ClearFiltersButton
                      onClick={() => change({ status: '', actionType: '', sort: '', view: '' })}
                    />
                  ),
                }
              : {})}
            title={
              filters.view === 'decidable'
                ? 'درخواستی منتظر تصمیم شما نیست'
                : filtered
                  ? 'درخواستی با این فیلترها پیدا نشد'
                  : 'هنوز درخواستی ثبت نشده است'
            }
          />
        }
        error={approvals.error}
        isEmpty={rows.length === 0}
        onRetry={() => void approvals.refetch()}
        skeleton={<ListSkeleton label="در حال بارگذاری درخواست‌ها…" />}
        status={listStatus(approvals)}
      >
        <AdminListTable
          caption="فهرست درخواست‌های تأیید"
          regionLabel="جدول درخواست‌های تأیید"
          columns={[
            {
              header: 'درخواست',
              id: 'request',
              pin: 'start',
              render: (row) => (
                <span className="saay-cell-main">
                  <Link state={OPENED_FROM_LIST} to={open(row)}>
                    {statusLabel('approvalActionType', row.actionType)}
                  </Link>
                  <small>{targetLabel(row)}</small>
                  {row.changeSummary === null ? null : (
                    <small className="admin-approval-change">{row.changeSummary}</small>
                  )}
                </span>
              ),
            },
            {
              header: 'ثبت‌کننده',
              id: 'requester',
              render: (row) => row.requestedBy.displayNameFa ?? 'بدون نام',
            },
            {
              header: 'مهلت تصمیم',
              id: 'expires',
              render: (row) => <ApprovalDeadline expiresAt={row.expiresAt} />,
              sortable: true,
            },
            {
              header: 'وضعیت',
              id: 'status',
              render: (row) => <ApprovalStatus decidable={row.decidable} status={row.status} />,
            },
          ]}
          mobileSummary={(row) => ({
            primary: (
              <span className="saay-cell-main">
                <strong>{statusLabel('approvalActionType', row.actionType)}</strong>
                <small>{targetLabel(row)}</small>
              </span>
            ),
            secondary: (
              <span className="admin-inline">
                <ApprovalStatus decidable={row.decidable} status={row.status} />
                {row.status === 'PENDING' ? <ApprovalDeadline expiresAt={row.expiresAt} /> : null}
              </span>
            ),
          })}
          onRowClick={(row) => void navigate(open(row), { state: OPENED_FROM_LIST })}
          onSortChange={(next) => change({ sort: next?.direction === 'asc' ? 'expiresAt' : '' })}
          rowKey={(row) => row.id}
          rows={rows}
          sort={filters.sort === 'expiresAt' ? { columnId: 'expires', direction: 'asc' } : null}
        />
        {approvals.isFetchNextPageError ? (
          <ErrorState error={approvals.error} onRetry={() => void approvals.fetchNextPage()} />
        ) : null}
        <ListFooter
          pager={
            <ListLoadMore
              hasMore={approvals.hasNextPage}
              loading={approvals.isFetchingNextPage}
              onLoadMore={() => void approvals.fetchNextPage()}
            />
          }
          shown={rows.length}
          total={undefined}
        />
      </QueryState>
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// Detail and decisions (A2–A5)

/** Where the target of a request lives in admin-web, when it has a screen. */
function targetPath(request: ApprovalRequest): string | undefined {
  if (request.targetType === 'MERCHANT') return `/merchants/${request.targetId}`;
  if (request.targetType === 'BUYER') return `/buyers/${request.targetId}`;
  if (
    request.targetType === 'PAYOUT_OWNER' &&
    request.impactReport.actionType === 'PAYOUT_ACCOUNT_CHANGE' &&
    request.impactReport.ownerType === 'MERCHANT'
  ) {
    return `/merchants/${request.targetId}?tab=payout`;
  }
  return undefined;
}

function Summary({ request }: { readonly request: ApprovalRequest }) {
  const path = targetPath(request);
  const target = targetLabel(request);
  return (
    <FactList
      items={[
        {
          key: 'requester',
          label: 'ثبت‌کننده',
          value: request.requestedBy.displayNameFa ?? 'بدون نام',
        },
        ...(path === undefined
          ? []
          : [
              {
                key: 'target-link',
                label: 'موضوع',
                value: <Link to={path}>{target}</Link>,
              },
            ]),
        {
          key: 'requested',
          label: 'زمان ثبت',
          value: <time dateTime={request.requestedAt}>{formatTimestamp(request.requestedAt)}</time>,
        },
        {
          key: 'expires',
          label: 'مهلت تصمیم',
          value: <time dateTime={request.expiresAt}>{formatTimestamp(request.expiresAt)}</time>,
        },
        { key: 'reason', label: 'دلیل درخواست', value: request.reason },
        ...(request.decidedBy === null
          ? []
          : [
              {
                key: 'decider',
                label: 'تصمیم‌گیرنده',
                value: request.decidedBy.displayNameFa ?? 'بدون نام',
              },
            ]),
        ...(request.decidedAt === null
          ? []
          : [
              {
                key: 'decided',
                label: 'زمان تصمیم',
                value: (
                  <time dateTime={request.decidedAt}>{formatTimestamp(request.decidedAt)}</time>
                ),
              },
            ]),
        ...(request.decisionReason === null
          ? []
          : [{ key: 'decision', label: 'توضیح تصمیم', value: request.decisionReason }]),
      ]}
    />
  );
}

type Mode = 'approve' | 'reject' | 'cancel';

const MODE_PATH: Readonly<Record<Mode, string>> = {
  approve: '/approval',
  cancel: '/cancellation',
  reject: '/rejection',
};

const MODE_COPY: Readonly<
  Record<Mode, { action: string; field: string; success: string; title: string }>
> = {
  approve: {
    action: 'تأیید و اجرا',
    field: 'توضیح تأیید (اختیاری)',
    success: 'درخواست تأیید و اجرا شد',
    title: 'تأیید درخواست',
  },
  cancel: {
    action: 'لغو درخواست',
    field: 'دلیل لغو',
    success: 'درخواست لغو شد',
    title: 'لغو درخواست',
  },
  reject: {
    action: 'رد درخواست',
    field: 'دلیل رد',
    success: 'درخواست رد شد',
    title: 'رد درخواست',
  },
};

/** Answers that mean the shown request is stale: it is read again from the server (AC §18.2.5). */
const RELOAD_CODES: ReadonlySet<string> = new Set([
  'APPROVAL_ALREADY_DECIDED',
  'APPROVAL_EXPIRED',
  'APPROVAL_ROLE_NOT_ALLOWED',
  'APPROVAL_SELF_DECISION_FORBIDDEN',
  'APPROVAL_SNAPSHOT_MISMATCH',
  'RESOURCE_VERSION_CONFLICT',
]);

/** The recovery line under the server message, by error code only (never by the message text). */
function decisionGuidance(error: unknown): string | undefined {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'APPROVAL_SELF_DECISION_FORBIDDEN':
      return typeof details.reason === 'string'
        ? statusLabel('approvalSelfDecisionReason', details.reason)
        : undefined;
    case 'APPROVAL_ROLE_NOT_ALLOWED': {
      const roles = Array.isArray(details.approverRoles)
        ? details.approverRoles.filter((role): role is string => typeof role === 'string')
        : [];
      return roles.length === 0
        ? undefined
        : `تصمیم این نوع درخواست با این نقش‌هاست: ${roles.map((role) => statusLabel('fixedRole', role)).join('، ')}.`;
    }
    case 'APPROVAL_ALREADY_DECIDED':
      return typeof details.status === 'string'
        ? `وضعیت فعلی درخواست: ${statusLabel('approvalRequestStatus', details.status)}. اطلاعات به‌روز شد.`
        : 'اطلاعات به‌روز شد.';
    case 'APPROVAL_EXPIRED':
      return 'مهلت این درخواست گذشته است و دیگر قابل تصمیم نیست.';
    case 'APPROVAL_SNAPSHOT_MISMATCH':
    case 'RESOURCE_VERSION_CONFLICT':
      return 'درخواست به‌روز شد؛ پیش از تصمیم، گزارش اثر را دوباره بررسی کنید.';
    default:
      return undefined;
  }
}

type DecisionBody = ApprovalDecision | ApprovalRejection | ApprovalCancellation;

function ApprovalDecisionFooter({
  request,
  self,
}: {
  readonly request: ApprovalRequest;
  readonly self: AdminSelf;
}) {
  const decidable = canDecide(self, request);
  const cancellable = canCancelApproval(self, request);
  const [panel, setPanel] = useState<Mode | 'idle'>('idle');
  const [commentOpen, setCommentOpen] = useState(false);
  const allowed = panel === 'cancel' ? cancellable : panel === 'idle' ? decidable : decidable;
  const mode = panel === 'idle' ? 'approve' : panel;
  return (
    <DecisionForm
      allowed={allowed}
      cancellable={cancellable}
      commentOpen={commentOpen}
      decidable={decidable}
      mode={mode}
      onClose={() => {
        setPanel('idle');
        setCommentOpen(false);
      }}
      onOpenComment={() => setCommentOpen(true)}
      onOpenPanel={setPanel}
      request={request}
      showIdleActions={panel === 'idle'}
    />
  );
}

function DecisionForm({
  allowed,
  cancellable,
  commentOpen,
  decidable,
  mode,
  onClose,
  onOpenComment,
  onOpenPanel,
  request,
  showIdleActions,
}: {
  /** False once a reload shows the request is no longer open to this caller (the form stays to explain why). */
  readonly allowed: boolean;
  readonly cancellable: boolean;
  readonly commentOpen: boolean;
  readonly decidable: boolean;
  readonly mode: Mode;
  readonly onClose: () => void;
  readonly onOpenComment: () => void;
  readonly onOpenPanel: (mode: Mode | 'idle') => void;
  readonly request: ApprovalRequest;
  readonly showIdleActions: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const copy = MODE_COPY[mode];
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const schema = z.object({ text: reasonSchema(mode !== 'approve') });
  const form = useForm<{ text: string }>({
    defaultValues: { text: '' },
    resolver: zodResolver(schema),
  });

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
    ]);
  }

  const command = useCommand<DecisionBody, ApprovalDecided>(
    `approval.${mode}`,
    (client, body, intent) =>
      client.post(approvalPath(request.id, MODE_PATH[mode]), { body, intent }),
    {
      onSuccess: async (result) => {
        queryClient.setQueryData(setupKeys.approval(request.id), result.data.approvalRequest);
        await refresh();
        // A decision changes the target (and bumps a merchant's version, AC §18.4).
        if (request.targetType === 'MERCHANT' || request.targetType === 'PAYOUT_OWNER') {
          await queryClient.invalidateQueries({ queryKey: setupKeys.merchants });
        }
        if (request.targetType === 'BUYER') {
          await queryClient.invalidateQueries({ queryKey: setupKeys.buyers });
        }
        toast.success({ confirmedBy: result, title: copy.success });
        onClose();
      },
    },
  );

  async function handle(outcome: CommandOutcome<ApprovalDecided>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(
      // The body field is `comment` (approve) or `reason` (reject, cancel); both are the one text box.
      applyFieldErrors(outcome.error, ['comment', 'reason'], (_name, error, options) =>
        form.setError('text', error, options),
      ),
    );
    if (RELOAD_CODES.has(outcome.error.code)) {
      await queryClient.invalidateQueries({ queryKey: setupKeys.approval(request.id) });
      await refresh();
    }
  }

  function bodyOf(text: string): DecisionBody {
    if (mode === 'cancel') {
      return { expectedVersion: request.version, reason: text };
    }
    if (mode === 'reject') {
      return { expectedVersion: request.version, reason: text, snapshotHash: request.snapshotHash };
    }
    return {
      expectedVersion: request.version,
      snapshotHash: request.snapshotHash,
      ...(text === '' ? {} : { comment: text }),
    };
  }

  async function submit(values: { text: string }): Promise<void> {
    setUnmapped([]);
    await handle(await command.submit(bodyOf(values.text.trim())));
  }

  const guidance =
    command.state.status === 'error' ? decisionGuidance(command.state.error) : undefined;

  const showCommentField = mode !== 'approve' || commentOpen;

  return (
    <form className="admin-approval-footer" noValidate onSubmit={form.handleSubmit(submit)}>
      {showCommentField ? (
        <>
          <TextArea
            error={form.formState.errors.text?.message}
            label={copy.field}
            maxLength={1000}
            {...form.register('text')}
          />
          {mode === 'approve' ? null : (
            <ReasonChips
              onChange={(value) => form.setValue('text', value, { shouldValidate: true })}
              reasons={reasonPresetsForIntent('approval.')}
              value={form.watch('text')}
            />
          )}
        </>
      ) : null}
      <FieldErrorList errors={unmapped} />
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        pendingLabel="در حال ثبت تصمیم…"
        state={command.state}
      />
      {guidance === undefined ? null : <Banner tone="warning">{guidance}</Banner>}
      {showIdleActions ? (
        <>
          {decidable && !commentOpen ? (
            <Button onClick={onOpenComment} type="button" variant="ghost">
              افزودن توضیح
            </Button>
          ) : null}
          <DialogActions
            primary={
              decidable ? (
                <Button
                  disabled={offline.blocked || !allowed}
                  loading={command.state.status === 'pending'}
                  type="submit"
                >
                  تأیید و اجرا
                </Button>
              ) : cancellable ? (
                <Button onClick={() => onOpenPanel('cancel')} type="button">
                  لغو درخواست
                </Button>
              ) : null
            }
            secondary={
              decidable ? (
                <Button onClick={() => onOpenPanel('reject')} type="button" variant="danger">
                  رد
                </Button>
              ) : null
            }
          />
        </>
      ) : (
        <DialogActions
          primary={
            <Button
              disabled={offline.blocked || !allowed}
              loading={command.state.status === 'pending'}
              type="submit"
              variant={mode === 'approve' ? 'primary' : 'danger'}
            >
              {copy.action}
            </Button>
          }
          secondary={
            <Button onClick={onClose} type="button" variant="secondary">
              بازگشت
            </Button>
          }
        />
      )}
      {offline.reason === undefined ? null : <p className="admin-muted">{offline.reason}</p>}
    </form>
  );
}

function ApprovalBody({
  request,
  self,
}: {
  readonly request: ApprovalRequest;
  readonly self: AdminSelf;
}) {
  const decidable = canDecide(self, request);
  const cancellable = canCancelApproval(self, request);
  const pending = request.status === 'PENDING';
  const selfRequested = pending && !decidable && request.requestedBy.userId === self.user.id;

  return (
    <div className="admin-sheet-stack">
      <p className="admin-sheet-meta">
        <ApprovalStatus decidable={request.decidable} status={request.status} />
        {selfRequested ? <StatusChip title="ثبت‌شده توسط شما" tone="info" /> : null}
      </p>
      {pending && !decidable && !cancellable && !selfRequested ? (
        <Banner tone="info">این درخواست در انتظار تصمیم مدیر مجاز است.</Banner>
      ) : null}
      <SurfaceSection id="approval-impact" level={3} title="گزارش اثر">
        <ImpactReport report={request.impactReport} />
      </SurfaceSection>
      <AccordionPanel
        items={[
          {
            content: <Summary request={request} />,
            key: 'summary',
            title: 'جزئیات درخواست',
          },
          {
            content: <StoredFields fields={request.payload} />,
            key: 'payload',
            summary: 'دستور ثبت‌شده',
            title: 'جزئیات فنی',
          },
        ]}
        label="اطلاعات تکمیلی درخواست"
      />
      {request.outcome === null ? null : (
        <SurfaceSection id="approval-outcome" level={3} title="نتیجهٔ اجرا">
          {targetPath(request) === undefined ? (
            <p className="text-sm text-muted-foreground">تغییر با موفقیت اعمال شد.</p>
          ) : (
            <ListRow
              end={<ChevronLeftIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
              icon={CircleCheckIcon}
              render={<Link to={targetPath(request)!} />}
              title={`مشاهدهٔ ${targetLabel(request)}`}
              tone="olive"
            />
          )}
        </SurfaceSection>
      )}
    </div>
  );
}

/** `/approvals/:approvalRequestId`: the request drawer over the inbox (A2), with A3–A5. */
export function ApprovalDetailSheet() {
  const { self } = useOutletContext<ApprovalsOutletContext>();
  const { approvalRequestId = '' } = useParams();
  const request = useApproval(approvalRequestId);
  const close = useCloseSheet('/approvals');
  const data = request.data;
  const title = data === undefined ? 'درخواست تأیید' : approvalSheetTitle(data);
  usePageTitle(title);
  const pending = data?.status === 'PENDING';
  const decidable = data !== undefined && canDecide(self, data);
  const cancellable = data !== undefined && canCancelApproval(self, data);
  const stickyFooter = pending === true && (decidable || cancellable);
  const eyebrow =
    data === undefined || !pending ? (
      'درخواست تأیید'
    ) : (
      <>
        درخواست تأیید · مهلت تصمیم{' '}
        <time dateTime={data.expiresAt}>{formatTimestamp(data.expiresAt)}</time>
      </>
    );
  return (
    <Dialog
      eyebrow={eyebrow}
      footer={
        data !== undefined && stickyFooter ? (
          <ApprovalDecisionFooter request={data} self={self} />
        ) : undefined
      }
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      stickyFooter={stickyFooter}
      title={title}
    >
      <QueryState
        error={request.error}
        onRetry={() => void request.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری درخواست…" lines={8} />}
        status={request.status}
      >
        {data === undefined ? null : <ApprovalBody request={data} self={self} />}
      </QueryState>
    </Dialog>
  );
}
